import React, { FC } from 'react';
import { getIn, useFormik } from 'formik';

import { Asset, HelpIcon } from '@clearcover/atlas';
import { SummarySection } from 'QuoteApp/screens/Summary/SummarySection';
import { useAppContext } from 'QuoteApp/services/providers';
import { transformTelematicsEnrollmentRequest } from 'QuoteApp/services/endpoints/PostTelematicsEnrollment';
import { mergeInProgressDataFromSummary } from 'QuoteApp/screens/Summary/Summary';
import { Column, FormInput, Row } from 'Shared/components';

import styles from './styles.module.css';

export const TelematicsEnrollment: FC = () => {
  const {
    appId,
    config,
    configLoading,
    details,
    setDetails,
    refreshRate,
    api,
  } = useAppContext();

  const fullFieldName = `${appId}.enrolled`;

  const getInitialValues = () => {
    const hasTelematicsInfo = !!details.telematics;
    const enrolled = details.telematics?.enrolled ? 'true' : 'false';

    return {
      [appId]: {
        enrolled: hasTelematicsInfo ? enrolled : undefined,
      },
    };
  };

  const {
    values,
    dirty,
    touched,
    isSubmitting,
    setFieldValue,
    getFieldProps,
    resetForm,
    submitForm,
    setSubmitting,
    setFieldTouched,
  } = useFormik({
    validateOnBlur: true,
    initialValues: getInitialValues(),
    onSubmit: async data => {
      try {
        const response = await api.saveAppData({
          data: data[appId],
          transformRequest: transformTelematicsEnrollmentRequest,
        });

        const mergedDetails = mergeInProgressDataFromSummary(
          details,
          response.data
        );
        setDetails(mergedDetails);
        refreshRate();
      } catch {
        resetForm();
      } finally {
        setSubmitting(false);
      }
    },
  });

  const onInputChange = async (value: 'true' | 'false') => {
    await setFieldValue(fullFieldName, value);
    submitForm();
  };

  const onInputBlur = async () => {
    setFieldTouched(fullFieldName, true);
    dirty && submitForm();
  };

  if (!config || configLoading || !config.telematics.enabled) {
    return null;
  }

  const shouldShowError = () => {
    const isRequired = config.telematics.required;
    const isTouched = getIn(touched, fullFieldName);
    const hasValue = getIn(values, fullFieldName);

    return isRequired && isTouched && !hasValue;
  };

  return (
    <SummarySection
      headerText="Clearcover ClearClaims+"
      subHeaderText="Customers can now receive an additional discount on their policy by enrolling in ClearClaims+."
      discountBadgeText="Discount Available"
    >
      <Row>
        <Column>
          <a
            href="https://resources.clearcover.com/hc/en-us/articles/27920745660951/live_preview/01JE95RPWK5B3Z7ATBDGEV2DC9"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            <Asset
              icon={HelpIcon}
              description="tooltip"
              height={16}
              width={16}
              className={styles.helpIcon}
            />
            Learn more about the ClearClaims+ Program
          </a>
        </Column>
      </Row>
      <Row className={styles.options}>
        <Column variant="half">
          <FormInput
            {...getFieldProps(fullFieldName)}
            data-testid="clear-claims-enrolled"
            variant="radioButtonGroup"
            options={{ true: 'Yes', false: 'No' }}
            errorMessage={shouldShowError() ? 'Required' : undefined}
            required={config.telematics.required}
            label="Does the customer wish to enroll in ClearClaims+?"
            loading={isSubmitting}
            disabled={isSubmitting}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </Column>
      </Row>
    </SummarySection>
  );
};
